import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Divider} from 'views/components';
import {Translate, translate} from 'views/module';
import {Phone, Mail} from 'views/icons';

const ReferencesView = (props) => {
  const {bioQuestions, setIsComplete} = props;

  const resolvedReferences = React.useMemo(() => {
    const entries = Array.isArray(bioQuestions?.references) ? bioQuestions.references : [];

    return entries.reduce((accumulator, entry) => {
      const name = typeof entry?.name === 'string' ? entry.name.trim() : '';

      if (!name) {
        return accumulator;
      }

      accumulator.push({
        name,
        relationship: typeof entry?.relationship === 'string' ? entry.relationship.trim() : '',
        phoneNumber: typeof entry?.phoneNumber === 'string' ? entry.phoneNumber.trim() : '',
        email: typeof entry?.email === 'string' ? entry.email.trim() : '',
      });

      return accumulator;
    }, []);
  }, [bioQuestions]);

  React.useEffect(() => {
    setIsComplete(resolvedReferences.length > 0);
  }, [resolvedReferences, setIsComplete]);

  if (resolvedReferences.length === 0) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.References.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} alignItems="stretch" sx={{width: '100%'}}>
      <Typography variant="subtitle1" sx={{fontWeight: 600}}>
        <Translate
          expression="caregiver.CaregiverBiography.References.Summary.Title"
          substitute={{
            count: resolvedReferences.length,
            references: translate(
                resolvedReferences.length === 1 ?
                  'caregiver.CaregiverBiography.References.Summary.ReferenceSingular' :
                  'caregiver.CaregiverBiography.References.Summary.ReferencePlural',
            ),
          }}
        />
      </Typography>
      <Divider />
      {
        resolvedReferences.map((entry, key) => {
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #0288d1',
                borderRadius: '12px',
                backgroundColor: '#e1f5fe',
                paddingY: 1,
                paddingX: 1.5,
                gap: 0.5,
              }}
            >
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 600}}>
                {entry.name}
                {entry.relationship && ` • ${entry.relationship}`}
              </Typography>
              {
                entry.phoneNumber && (
                  <Box sx={{color: '#0288d1'}}>
                    <Stack direction='row' gap={1}>
                      <Phone fontSize='small' />
                      <Typography>{entry.phoneNumber}</Typography>
                    </Stack>
                  </Box>
                )
              }
              {
                entry.email && (
                  <Box sx={{color: '#0288d1'}}>
                    <Stack direction='row' gap={1}>
                      <Mail fontSize='small' />
                      <Typography>{entry.email}</Typography>
                    </Stack>
                  </Box>
                )
              }
            </Box>
          );
        })
      }
    </Stack>
  );
};

ReferencesView.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
};

export default React.memo(ReferencesView);
